// The client half of the `apierr` envelope (`SC-F200`, `SC-S208`, `06` §1.4).
//
// Every 4xx and 5xx Sluice Cloud answers has the same body — `{code, message,
// request_id, details}` — and this module is the single place that turns it,
// or anything the PocketBase SDK throws, into one typed [ApiError]. Two rules
// from `internal/apierr` carry straight over:
//
//   - The code catalogue is CLOSED and each code is public contract: it is
//     never renamed and never changes meaning. [ApiErrorCode] below is a
//     transcript of `06` §1.4.1 and of `internal/apierr/apierr.go`; the two
//     lists move together or not at all.
//   - `request_id` is on EVERY response, successes included (`OD-11`). It is
//     the field a support engineer asks for, which is why [ApiError] carries it
//     as a first-class property and [supportReference] exists to render it.
//
// Codes originating inside a managed Sluice instance are NOT translated: the
// Cloud answers `instance_error` and passes Sluice's own `ERR_*` code through
// in `details.sluice`, so a customer who greps for `ERR_MASK_UNSUPPORTED_CONTEXT`
// finds it (`06` §1.4). [sluiceError] reads that object.

/**
 * ApiErrorCode is the closed catalogue of `06` §1.4.1. A response carrying a
 * code outside this union is a newer server than this bundle: `06` §1.3 makes
 * new error codes an additive, non-breaking change, so [errorMessage] falls
 * back to the server's own message rather than pretending the failure is
 * unknown.
 */
export type ApiErrorCode =
  | "unauthorized"
  | "invalid_token"
  | "forbidden"
  | "insufficient_scope"
  | "verification_required"
  | "verification_invalid"
  | "otp_invalid"
  | "not_found"
  | "validation_failed"
  | "name_taken"
  | "conflict"
  | "precondition_failed"
  | "idempotency_key_reuse"
  | "field_guarded"
  | "quota_exceeded"
  | "rate_limited"
  | "payload_too_large"
  | "unsupported_media_type"
  | "instance_not_ready"
  | "instance_suspended"
  | "instance_busy"
  | "restart_required"
  | "publish_failed"
  | "policy_invalid"
  | "draft_empty"
  | "image_not_allowed"
  | "capacity_unavailable"
  | "orchestrator_unavailable"
  | "proxy_unavailable"
  | "job_not_cancellable"
  | "audit_unavailable"
  | "instance_error"
  | "service_draining"
  | "internal";

/**
 * ErrorEnvelope is the wire shape, field for field with
 * `apierr.Envelope` (`internal/apierr/apierr.go`). `SC-S208` requires it to
 * have no other top-level keys. `request_id` is omitted only in one place: a
 * job record stores "the envelope minus request_id" in its `error` field,
 * because a job outlives the request that created it (`06` §1.8).
 */
export interface ErrorEnvelope {
  code: ApiErrorCode | string;
  message: string;
  request_id?: string;
  details?: Record<string, unknown>;
}

/** ValidationField is one entry of `details.fields` for `validation_failed`:
 * the JSON path that failed and why. A path rather than a name because request
 * bodies nest, and a form that highlights the wrong input is worse than one
 * that highlights none (`06` §1.4.1). */
export interface ValidationField {
  path: string;
  reason: string;
}

/** SluiceError is a managed instance's own error, carried under
 * `details.sluice` untranslated. `code` belongs to Sluice's `ERR_*` catalogue,
 * which is part of Sluice's public contract and not ours to re-case. */
export interface SluiceError {
  code: string;
  message: string;
  status: number;
}

/** QuotaDetail is `details` for `quota_exceeded` (`SC-F901`): which limit was
 * hit and how far over the request would have gone. */
export interface QuotaDetail {
  quota: string;
  limit: number;
  current: number;
}

/** RoleDetail is `details` for `forbidden`: the role the route requires and the
 * role the caller holds, so the copy can say "this needs maintainer; you are a
 * member" instead of "forbidden" (`06` §2.4, `SC-S103`). */
export interface RoleDetail {
  required_role: string;
  actual_role: string;
}

/**
 * ApiError is every failure the UI handles, whatever produced it. `message` is
 * already the human-facing copy (see [errorMessage]); `serverMessage` keeps the
 * server's own wording for diagnostics, and `requestId` is what the reader
 * quotes when they open a support ticket.
 */
export class ApiError extends Error {
  /** The catalogue code. Branch on this, never on `message`. */
  readonly code: ApiErrorCode | string;
  /** `request_id` from the body, or the `X-Request-Id` header. Empty only when
   * the failure never reached the server (offline, DNS, aborted fetch). */
  readonly requestId: string;
  /** HTTP status, when the failure came from a response at all. */
  readonly status: number | undefined;
  /** The code-specific `details` object. Never carries secret material — the
   * same rule `internal/audit` enforces on metadata (`06` §1.4). */
  readonly details: Record<string, unknown> | undefined;
  /** The server's own `message`, before the client-side copy map. */
  readonly serverMessage: string;

  constructor(init: {
    code: ApiErrorCode | string;
    message?: string;
    requestId?: string;
    status?: number;
    details?: Record<string, unknown>;
  }) {
    super(errorMessage(init.code, init.message));
    this.name = "ApiError";
    this.code = init.code;
    this.serverMessage = init.message ?? "";
    this.requestId = init.requestId ?? "";
    this.status = init.status;
    this.details = init.details;
  }
}

// code -> the product's own copy. Every code in `06` §1.4.1 is here, in the
// same order, so a reviewer can diff the two lists by eye. The wording follows
// the naming discipline: "organization", "instance", "subject" — never
// "tenant", "team" or "account" for the tenancy root.
const MESSAGES: Record<string, string> = {
  unauthorized: "Please sign in to continue.",
  invalid_token: "That API key is not valid, or it has expired or been revoked.",
  forbidden: "Your role on this organization does not allow this action.",
  insufficient_scope: "This API key's scope does not cover that request.",
  verification_required: "This action needs a quick identity check. Verify with a passkey or an email code.",
  verification_invalid: "That verification has expired or was already used. Please verify again.",
  otp_invalid: "That code is not valid or has expired. Check your email, or request a new code.",
  not_found: "That record does not exist, or you do not have access to it.",
  validation_failed: "Some of the values are invalid. Check the highlighted fields and try again.",
  name_taken: "That name is already in use. Choose a different one.",
  conflict: "Someone else changed this since you loaded it. Reload and try again.",
  precondition_failed: "This record changed since you opened it. Reload and re-apply your edit.",
  idempotency_key_reuse: "That retry sent a different request than the original. Start the action again.",
  field_guarded: "One or more of those settings are managed by Sluice Cloud and cannot be set here.",
  quota_exceeded: "This action would exceed your organization's quota.",
  rate_limited: "Too many requests — please wait a moment and try again.",
  payload_too_large: "That is larger than this endpoint accepts.",
  unsupported_media_type: "The request had the wrong content type.",
  instance_not_ready: "The instance is not ready for this operation yet. Wait for it to become healthy.",
  instance_suspended: "The instance is suspended. Resume it first.",
  instance_busy: "Another operation is already running on this instance. Wait for it to finish.",
  restart_required: "This change restarts the instance. Confirm the restart to publish it.",
  publish_failed: "The instance did not confirm the published configuration. The previous generation is still live.",
  policy_invalid: "The draft was rejected. Review the findings and fix them before publishing.",
  draft_empty: "The draft has no changes to publish.",
  image_not_allowed: "That Sluice version is not in the allowed list.",
  capacity_unavailable: "The cluster cannot place this instance right now. Please try again shortly.",
  orchestrator_unavailable: "The orchestration component is unreachable. Reading is unaffected; try the change again shortly.",
  proxy_unavailable: "The command could not be started in time. Please try again.",
  job_not_cancellable: "This job can no longer be cancelled.",
  audit_unavailable: "The audit store is unreachable. Please try again shortly.",
  instance_error: "The instance refused the request.",
  service_draining: "Sluice Cloud is restarting. Please try again in a moment.",
  internal: "Something went wrong on our side. Please try again.",
};

/**
 * errorMessage maps a catalogue code to the product's own copy. An unknown code
 * — a newer server, which `06` §1.3 explicitly permits — falls back to the
 * server's `message`, which `apierr` guarantees is human-readable and
 * client-safe, and finally to a generic line.
 */
export function errorMessage(code: string, serverMessage?: string): string {
  const known = MESSAGES[code];
  if (known) return known;
  if (serverMessage && serverMessage.trim() !== "") return serverMessage;
  return "Something went wrong. Please try again.";
}

/**
 * parseError normalizes anything thrown on an API path into an [ApiError]:
 * the envelope `api/http.ts` already parsed, a PocketBase `ClientResponseError`
 * (which carries the parsed body on `.response`/`.data` and the status on
 * `.status`), a bare `{code, message}` thrown by a ceremony helper, or a
 * network `TypeError`. It never throws and never returns undefined, so a
 * `catch` block has exactly one shape to handle.
 */
export function parseError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;

  if (e && typeof e === "object") {
    const any = e as Record<string, unknown>;
    const status = typeof any.status === "number" ? any.status : undefined;
    const body = (any.response ?? any.data) as Record<string, unknown> | undefined;
    if (body && typeof body === "object") {
      const parsed = fromEnvelope(body, status);
      if (parsed) return parsed;
    }
    const direct = fromEnvelope(any, status);
    if (direct) return direct;
    if (status !== undefined) {
      return new ApiError({ code: codeForStatus(status), status });
    }
  }

  return new ApiError({
    code: "internal",
    message: e instanceof Error ? e.message : String(e ?? ""),
  });
}

/** humanError is the convenience composition for a toast: parse, then render. */
export function humanError(e: unknown): string {
  return parseError(e).message;
}

/**
 * supportReference renders the request id as the one line a reader can paste
 * into a support ticket, or the empty string when the failure never reached the
 * server. `OD-11` was adopted precisely so this is always available: retrofitting
 * `request_id` later would have meant editing every error site in the codebase.
 */
export function supportReference(e: unknown): string {
  const id = parseError(e).requestId;
  return id ? `Reference ${id}` : "";
}

/**
 * errorDetail reads one key out of the code-specific `details` object. Each
 * code fixes its own shape in `06` §1.4.1, so the caller supplies the type it
 * expects for the code it just branched on:
 *
 *   if (err.code === "instance_busy") {
 *     const jobId = errorDetail<string>(err, "job_id");
 *   }
 */
export function errorDetail<T = unknown>(e: unknown, key: string): T | undefined {
  const details = parseError(e).details;
  if (!details) return undefined;
  return details[key] as T | undefined;
}

/**
 * validationFields returns `details.fields` for a `validation_failed`, or an
 * empty array. This is the one accessor every form in the product uses: `06`
 * §1.4.1 fixes the `[{path, reason}]` shape package-wide so a single renderer
 * can highlight the offending inputs everywhere.
 */
export function validationFields(e: unknown): ValidationField[] {
  const raw = errorDetail<unknown>(e, "fields");
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (f): f is ValidationField =>
      !!f && typeof f === "object" && typeof (f as ValidationField).path === "string",
  );
}

/**
 * sluiceError returns the managed instance's own error from an `instance_error`
 * response, or null. Show its `code` verbatim — it is Sluice's stable `ERR_*`
 * contract, and translating it would break the customer who greps for it.
 */
export function sluiceError(e: unknown): SluiceError | null {
  const raw = errorDetail<unknown>(e, "sluice");
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<SluiceError>;
  if (typeof s.code !== "string") return null;
  return { code: s.code, message: s.message ?? "", status: s.status ?? 0 };
}

/** fromEnvelope builds an ApiError from a body that looks like the envelope,
 * or returns null when it does not (so the caller can keep trying). */
function fromEnvelope(body: Record<string, unknown>, status?: number): ApiError | null {
  const code = typeof body.code === "string" ? body.code : "";
  const message = typeof body.message === "string" ? body.message : "";
  if (!code && !message) return null;
  const requestId = typeof body.request_id === "string" ? body.request_id : undefined;
  const details =
    body.details && typeof body.details === "object"
      ? (body.details as Record<string, unknown>)
      : undefined;
  return new ApiError({
    code: code || codeForStatus(status),
    message,
    requestId,
    status,
    details,
  });
}

/**
 * codeForStatus is the client mirror of `apierr.CodeForStatus`: the code a
 * response of that status carries when the body did not name one. It is the
 * inverse of the catalogue's status column over the statuses it covers.
 * Statuses the catalogue reaches through several codes map to the generic
 * member of the group; 400 is deliberately absent on the server side, and the
 * client answers `internal` for it rather than guessing between `otp_invalid`
 * and `image_not_allowed`.
 */
function codeForStatus(status?: number): ApiErrorCode {
  switch (status) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 412:
      return "precondition_failed";
    case 413:
      return "payload_too_large";
    case 415:
      return "unsupported_media_type";
    case 422:
      return "validation_failed";
    case 429:
      return "rate_limited";
    case 502:
      return "instance_error";
    case 503:
      return "service_draining";
    default:
      return "internal";
  }
}
